import { useEffect, useRef } from 'react';

/**
 * Makes a horizontally-scrolling container usable with a mouse: vertical
 * wheel delta scrolls it sideways, and click-and-drag pans it — mirrors the
 * touch-swipe gesture these carousels were originally built for.
 */
export function useHorizontalDragScroll() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // only while there is something off to the side (e.g. not when the library is a grid)
    const canScroll = () => el.scrollWidth > el.clientWidth + 1;

    const handleWheel = (e) => {
      if (!canScroll()) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // let native horizontal scroll/trackpad through
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    let isDragging = false;
    let dragMoved = false;
    let startX = 0;
    let startScrollLeft = 0;

    const handleMouseDown = (e) => {
      if (!canScroll()) return;
      isDragging = true;
      dragMoved = false;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
    };
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 5) dragMoved = true;
      el.scrollLeft = startScrollLeft - dx;
    };
    const endDrag = () => {
      isDragging = false;
      el.style.cursor = '';
    };
    // Swallow the click that would otherwise fire on mouseup after a drag
    // (prevents accidentally opening a comic while just panning the row).
    const handleClickCapture = (e) => {
      if (dragMoved) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endDrag);
    el.addEventListener('click', handleClickCapture, true);

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', endDrag);
      el.removeEventListener('click', handleClickCapture, true);
    };
  }, []);

  return ref;
}
